import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Alert } from '@mui/material';
import { useQuery, useSubscription } from '@apollo/client';
import { isEmpty } from 'lodash';
import AuthContext from '../../../auth/context/AuthContext';
import clientGraphql from '../../../../common/apollo-graphql';
import Referral from '../../../referral/models/Referral';
import GlobalLoader from '../../../common/components/loader/GlobalLoader';

const isValidUuid = (value) => typeof value === 'string' && value.trim() !== '';

export default function ActiveReferrals({
  query,
  uuid,
  subscription,
  tableComponent: TableComponent,
  buildActions,
  renderDialogs,
  withFaxFilter = false,
  initialSearchFilters = {
    type: 'Name',
    patientName: '',
    birthdate: '',
  },
  tableProps = {},
  isBusy = false,
  subscriptionClinicIdFallback = null,
  notifyOnNetworkStatusChange = false,
  shouldShowGlobalLoader = ({ isBusy: busy }) => busy,
}) {
  const currentUser = useContext(AuthContext);

  const [parsedData, setParsedData] = useState({ rows: [], count: 0 });
  const [searchFilters, setSearchFilters] = useState(initialSearchFilters);
  const [closeError, setCloseError] = useState(false);

  const subscriptionsRef = useRef([]);
  const debounceRef = useRef(null);
  const searchFiltersRef = useRef(initialSearchFilters);
  const refetchDataRef = useRef(() => {});

  useEffect(() => {
    searchFiltersRef.current = searchFilters;
  }, [searchFilters]);

  const initialVariables = useMemo(
    () => ({
      uuid,
      patientName: null,
      birthdate: null,
      ...(withFaxFilter ? { isFax: null } : {}),
    }),
    [uuid, withFaxFilter]
  );

  const { loading, error, data, refetch, networkStatus } = useQuery(query, {
    client: clientGraphql,
    variables: initialVariables,
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    skip: !isValidUuid(uuid),
    notifyOnNetworkStatusChange,
  });

  const { data: subscriptionData } = useSubscription(subscription, {
    variables: { clinic_id: currentUser?.clinics?.[0]?.id ?? subscriptionClinicIdFallback },
  });

  const buildParsedData = useCallback(
    (rows) => ({
      rows: rows.map((item) => ({
        ...item,
        user: currentUser,
        referral: new Referral(item),
        actions: buildActions(item, {
          refresh: () => refetchDataRef.current(searchFiltersRef.current),
        }),
      })),
      count: rows.length,
    }),
    [buildActions, currentUser]
  );

  const refetchData = useCallback(
    (filters) => {
      if (!refetch) return;
      if (debounceRef.current) clearTimeout(debounceRef.current);
      if (!isValidUuid(uuid)) return;

      debounceRef.current = setTimeout(() => {
        refetch({
          uuid,
          patientName: filters.patientName || null,
          birthdate: filters.birthdate || null,
          ...(withFaxFilter
            ? {
                isFax: filters.filterFax === 'fax' ? true : filters.filterFax === 'none' ? false : null,
              }
            : {}),
        }).then(({ data: refetchedData }) => {
          const rows = refetchedData?.rows || [];
          if (!isEmpty(rows)) {
            setParsedData(buildParsedData(rows));
          } else {
            setParsedData({ rows: [], count: 0 });
          }
        });
      }, 400);
    },
    [refetch, uuid, withFaxFilter, buildParsedData]
  );

  useEffect(() => {
    refetchDataRef.current = refetchData;
  }, [refetchData]);

  const refresh = useCallback(() => {
    refetchData(searchFiltersRef.current);
  }, [refetchData]);

  const handleFilterChanged = useCallback(
    (e) => {
      const value = e.target.value;
      const newFilters = { ...searchFilters };
      if (searchFilters.type === 'Name') newFilters.patientName = value;
      else newFilters.birthdate = value;

      setSearchFilters(newFilters);
      refetchData(newFilters);
    },
    [searchFilters, refetchData]
  );

  const handleChangeDropdown = useCallback(
    (e) => {
      const newFilters = {
        type: e.target.value,
        patientName: '',
        birthdate: '',
        ...(withFaxFilter ? { filterFax: searchFilters.filterFax } : {}),
      };
      setSearchFilters(newFilters);
      refetchData(newFilters);
    },
    [searchFilters, refetchData, withFaxFilter]
  );

  const handleFilterFaxChanged = useCallback(
    (e) => {
      if (!withFaxFilter) return;
      const newFilters = { ...searchFilters, filterFax: e.target.value };
      setSearchFilters(newFilters);
      refetchData(newFilters);
    },
    [searchFilters, refetchData, withFaxFilter]
  );

  useEffect(() => {
    if (!isEmpty(data) && Array.isArray(data.rows)) {
      if (!isEmpty(data.rows)) {
        setParsedData(buildParsedData(data.rows));
      } else {
        setParsedData({ rows: [], count: 0 });
      }
    }
  }, [data, buildParsedData]);

  useEffect(() => {
    if (!isEmpty(subscriptionData)) {
      if (
        !isEmpty(subscriptionsRef.current) &&
        JSON.stringify(subscriptionData) !== JSON.stringify(subscriptionsRef.current)
      ) {
        refetchData(searchFiltersRef.current);
      }
      subscriptionsRef.current = subscriptionData;
    }
  }, [subscriptionData, refetchData]);

  if (shouldShowGlobalLoader({ loading, networkStatus, isBusy })) return <GlobalLoader />;

  const extraTableProps = typeof tableProps === 'function' ? tableProps({ refresh, searchFilters }) : tableProps;

  return (
    <>
      {error && !closeError && (
        <Alert variant="filled" severity="error" onClose={() => setCloseError(true)}>
          We are sorry, but we are having difficulty with our system. We are unable to load data at
          the moment. Please reach out to us if you continue to experience this issue.
        </Alert>
      )}

      <TableComponent
        onFilterChanged={handleFilterChanged}
        onChangeDropdown={handleChangeDropdown}
        {...(withFaxFilter ? { onFilterFaxChanged: handleFilterFaxChanged } : {})}
        loading={loading}
        refresh={refresh}
        data={!isEmpty(parsedData) ? parsedData : { rows: [], count: 0 }}
        {...extraTableProps}
      />

      {renderDialogs?.({ refresh, searchFilters })}
    </>
  );
}
